import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrainingChat } from "@/components/training-chat";
import { api } from "@/lib/api";
import { AppShell } from "@/components/app-shell";

const CATEGORY_NAMES: Record<string, string> = {
  deposit: "Deposit Problems",
  withdrawal: "Withdrawal Issues",
  bonus: "Bonus & Promotions",
  account: "Account Issues",
  game: "Game Issues",
  general: "General Questions",
};

type TrainingMode = "sequential" | "random" | "browse";

interface ConversationListItem {
  id: string;
  preview_text: string;
  date: string;
  completed: boolean;
}

function BrowseList({
  categoryId,
  onSelect,
  onBack,
}: {
  categoryId: string;
  onSelect: (id: string) => void;
  onBack: () => void;
}) {
  const [items, setItems] = useState<ConversationListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const pageSize = 50;

  const loadPage = useCallback(
    async (p: number) => {
      setLoading(true);
      try {
        const data = await api<{
          conversations: ConversationListItem[];
          total: number;
        }>(`/api/conversation?category=${categoryId}&mode=list&page=${p}`);
        setItems(data.conversations || []);
        setTotal(data.total || 0);
        setPage(p);
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    },
    [categoryId],
  );

  useEffect(() => {
    loadPage(1);
  }, [loadPage]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-4 flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          ← Back
        </Button>
        <h2 className="text-lg font-semibold">Browse Conversations</h2>
        {total > 0 && <span className="ml-auto text-sm text-muted-foreground">{total} total</span>}
      </div>

      <Card className="overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-40 text-gray-500">Loading...</div>
          ) : items.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-gray-500">
              No conversations found.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => onSelect(item.id)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3"
                >
                  <span className="text-xs text-gray-400 w-8 shrink-0 text-right">
                    {(page - 1) * pageSize + index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 truncate">
                      {item.preview_text || "(no preview)"}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">{item.date}</p>
                  </div>
                  {item.completed && (
                    <span className="shrink-0 text-green-600 text-sm font-medium flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Done
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </Card>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => loadPage(page - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => loadPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

function ModeSelection({
  categoryId,
  categoryName,
  onSelect,
  onBrowse,
}: {
  categoryId: string;
  categoryName: string;
  onSelect: (mode: TrainingMode) => void;
  onBrowse: () => void;
}) {
  const navigate = useNavigate();
  const [progressInfo, setProgressInfo] = useState<{ completed: number; total: number } | null>(null);
  const [loadingProgress, setLoadingProgress] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api<{ total?: number; completed?: number }>(
      `/api/conversation?category=${categoryId}&mode=list&page=1`,
    )
      .then((data) => {
        if (cancelled) return;
        if (data.total !== undefined && data.completed !== undefined) {
          setProgressInfo({ completed: data.completed, total: data.total });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingProgress(false);
      });
    return () => {
      cancelled = true;
    };
  }, [categoryId]);

  return (
    <div className="mx-auto flex w-full max-w-md flex-col items-stretch py-8">
      <div className="mb-8 text-center">
        <button
          onClick={() => navigate("/")}
          className="mb-4 inline-block text-sm text-muted-foreground hover:text-foreground"
        >
          ← Change Category
        </button>
        <h1 className="text-2xl font-semibold tracking-tight">{categoryName}</h1>
        <p className="mt-1 text-sm text-muted-foreground">Choose how you want to train</p>
      </div>

        <div className="space-y-3">
          <button
            onClick={() => onSelect("sequential")}
            className="w-full bg-green-600 hover:bg-green-700 text-white rounded-lg px-5 py-4 text-left transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-base">Continue Training</p>
                <p className="text-green-100 text-sm mt-0.5">
                  Work through conversations in order
                </p>
              </div>
              {!loadingProgress && progressInfo && (
                <span className="text-sm font-medium bg-green-500 px-2.5 py-1 rounded-full whitespace-nowrap">
                  {progressInfo.completed}/{progressInfo.total}
                </span>
              )}
            </div>
          </button>

          <button
            onClick={() => onSelect("random")}
            className="w-full border border-gray-300 bg-white hover:bg-gray-50 text-gray-800 rounded-lg px-5 py-4 text-left transition-colors"
          >
            <p className="font-semibold text-base">Random Practice</p>
            <p className="text-gray-500 text-sm mt-0.5">
              Get a random uncompleted conversation
            </p>
          </button>

        <button
          onClick={onBrowse}
          className="w-full border border-gray-300 bg-white hover:bg-gray-50 text-gray-800 rounded-lg px-5 py-4 text-left transition-colors"
        >
          <p className="font-semibold text-base">Browse All</p>
          <p className="text-gray-500 text-sm mt-0.5">
            See all conversations and pick one
          </p>
        </button>
      </div>
    </div>
  );
}

export default function TrainPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const categoryId = searchParams.get("category") || "general";
  const conversationId = searchParams.get("id") || undefined;
  const categoryName = CATEGORY_NAMES[categoryId] || "General Questions";

  const [view, setView] = useState<"select" | "browse" | "training">(
    conversationId ? "training" : "select",
  );
  const [selectedMode, setSelectedMode] = useState<TrainingMode>("random");
  const [selectedConversationId, setSelectedConversationId] = useState<string | undefined>(
    conversationId,
  );

  function handleModeSelect(mode: TrainingMode) {
    setSelectedMode(mode);
    setSelectedConversationId(undefined);
    setView("training");
  }

  function handleBrowseSelect(id: string) {
    setSelectedMode("browse");
    setSelectedConversationId(id);
    setView("training");
  }

  function handleChangeCategory() {
    navigate("/");
  }

  function handleBackToSelect() {
    setView("select");
    setSelectedConversationId(undefined);
  }

  const crumbs = [
    { label: "Train", to: undefined as string | undefined },
    { label: categoryName },
  ];

  if (view === "select") {
    return (
      <AppShell breadcrumbs={crumbs}>
        <ModeSelection
          categoryId={categoryId}
          categoryName={categoryName}
          onSelect={handleModeSelect}
          onBrowse={() => setView("browse")}
        />
      </AppShell>
    );
  }

  if (view === "browse") {
    return (
      <AppShell
        breadcrumbs={[...crumbs, { label: "Browse" }]}
        actions={
          <Button variant="outline" size="sm" onClick={() => setView("select")}>
            ← Back
          </Button>
        }
      >
        <BrowseList
          categoryId={categoryId}
          onSelect={handleBrowseSelect}
          onBack={() => setView("select")}
        />
      </AppShell>
    );
  }

  return (
    <AppShell breadcrumbs={[...crumbs, { label: "Session" }]} bleed>
      <div className="h-[calc(100vh-3.5rem)] p-4">
        <Card className="mx-auto h-full max-w-4xl flex flex-col overflow-hidden">
          <TrainingChat
            categoryId={categoryId}
            categoryName={categoryName}
            conversationId={selectedConversationId}
            mode={selectedMode}
            onChangeCategory={handleChangeCategory}
            onBackToModeSelect={handleBackToSelect}
          />
        </Card>
      </div>
    </AppShell>
  );
}
